/**
 * Dropdown Cache Service - Caching for dropdown configuration data
 * 
 * Wraps the ABP proxy DropDownConfigService with caching.
 * Components should still use DropDownConfigService for CRUD operations,
 * but call refresh() on this service after any create/update/delete.
 * 
 * Features:
 * - Loads all dropdown configurations once
 * - Quick lookup by configuration ID or name
 * - Manual refresh after CRUD operations
 */

import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DropDownConfigService } from '@proxy/configurations';

export interface DropdownConfigItem {
  id?: string;
  name?: string;
  dropItemList?: any[];
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class DropdownCacheService {
  private isLoading = false;
  private isLoaded = false;
  
  // Cached dropdown configurations
  private cachedConfigs: DropdownConfigItem[] = [];
  private configById: Map<string, DropdownConfigItem> = new Map();
  private configByName: Map<string, DropdownConfigItem> = new Map();

  constructor(private readonly dropDownConfigProxy: DropDownConfigService) {}

  /**
   * Get all dropdown configurations (async)
   */
  async getAll(): Promise<DropdownConfigItem[]> {
    if (!this.isLoaded && !this.isLoading) {
      await this.loadConfigs();
    }
    return this.cachedConfigs;
  }

  /**
   * Get dropdown configurations synchronously (empty if not loaded)
   */
  getCachedConfigs(): DropdownConfigItem[] {
    return this.cachedConfigs;
  }

  /**
   * Get dropdown items by configuration ID
   */
  getDropdownItems(configId: string): any[] | null {
    if (configId === 'String') return null;
    const config = this.configById.get(configId);
    return config?.dropItemList || null;
  }

  /**
   * Get dropdown items by configuration name
   */
  getDropdownItemsByName(name: string): any[] | null {
    const config = this.configByName.get(name);
    return config?.dropItemList || null;
  }

  /**
   * Resolve a dropdown item ID to its label
   * Searches across all dropdown configurations
   */
  resolveDropdownItemLabel(itemId: string): string | null {
    if (!itemId) return null;
    
    for (const config of this.cachedConfigs) {
      if (!config.dropItemList) continue;
      const item = config.dropItemList.find(i => i.id === itemId);
      if (item) {
        return item.label;
      }
    }
    return null;
  }

  /**
   * Resolve a dropdown item ID to its label within a specific configuration
   */
  resolveDropdownItemLabelInConfig(configId: string, itemId: string): string | null {
    if (!itemId || !configId) return null;
    
    const config = this.configById.get(configId);
    if (!config?.dropItemList) return null;
    
    const item = config.dropItemList.find(i => i.id === itemId);
    return item?.label || null;
  }

  /**
   * Check if cache is ready
   */
  isReady(): boolean {
    return this.isLoaded;
  }

  /**
   * Force refresh the cache - call after any CRUD operation
   */
  async refresh(): Promise<void> {
    this.isLoaded = false;
    await this.loadConfigs();
  }

  /**
   * Initialize cache (call on app startup or component init)
   */
  async init(): Promise<void> {
    if (!this.isLoaded && !this.isLoading) {
      await this.loadConfigs();
    }
  }

  /**
   * Load all dropdown configurations
   */
  private async loadConfigs(): Promise<void> {
    if (this.isLoading) return;
    
    this.isLoading = true;
    
    try {
      const response = await firstValueFrom(
        this.dropDownConfigProxy.getList({ maxResultCount: 1000 })
      );
      
      this.cachedConfigs = response.items || [];
      this.configById.clear();
      this.configByName.clear();
      
      for (const config of this.cachedConfigs) {
        if (config.id) {
          this.configById.set(config.id, config);
        }
        if (config.name) {
          this.configByName.set(config.name, config);
        }
      }
      
      this.isLoaded = true;
    } catch (error) {
      console.error('Error loading dropdown configs:', error);
      this.cachedConfigs = [];
      this.configById.clear();
      this.configByName.clear();
    } finally {
      this.isLoading = false;
    }
  }
}
